import classNames from 'classnames';

import { StarFilledIcon } from 'fluxo-ui/icons';

import { LiveQuizLeaderboardEntry } from '../../../types/index.ts';

import Podium from './Podium';

interface LeaderboardProps {
    entries: LiveQuizLeaderboardEntry[];
    highlightParticipantId?: string | null;
    showPodium?: boolean;
    limit?: number;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ entries, highlightParticipantId, showPodium = false, limit = 10 }) => {
    if (entries.length === 0) {
        return (
            <p className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                No scores yet — the leaderboard fills up as answers come in.
            </p>
        );
    }

    const visible = entries.slice(0, limit);
    const listStart = showPodium ? 3 : 0;

    return (
        <div className="space-y-6">
            {showPodium && <Podium entries={entries} highlightParticipantId={highlightParticipantId} />}

            <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                {visible.slice(listStart).map((entry) => {
                    const isMe = entry.participantId === highlightParticipantId;

                    return (
                        <li
                            key={entry.participantId}
                            className={classNames('flex items-center gap-3 py-2.5 px-2 -mx-2 rounded', {
                                'bg-teal-50 dark:bg-teal-950/40': isMe,
                            })}
                        >
                            <span className="w-7 shrink-0 text-sm font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                                {entry.rank}
                            </span>

                            <span
                                className={classNames('flex-1 min-w-0 truncate text-sm', {
                                    'font-semibold text-slate-900 dark:text-white': isMe,
                                    'text-slate-700 dark:text-slate-300': !isMe,
                                })}
                            >
                                {entry.name}
                                {isMe && <span className="ml-1.5 text-xs text-teal-700 dark:text-teal-400">you</span>}
                            </span>

                            {entry.streak > 1 && (
                                <span className="flex items-center gap-0.5 shrink-0 text-xs font-medium text-orange-600 dark:text-orange-400">
                                    <StarFilledIcon className="w-3.5 h-3.5" />
                                    {entry.streak}
                                </span>
                            )}

                            {entry.scoreDelta > 0 && (
                                <span className="shrink-0 text-xs font-medium tabular-nums text-emerald-600 dark:text-emerald-400">
                                    +{entry.scoreDelta}
                                </span>
                            )}

                            <span className="w-16 shrink-0 text-right text-sm font-bold tabular-nums text-slate-900 dark:text-white">
                                {entry.score}
                            </span>
                        </li>
                    );
                })}
            </ul>

            {entries.length > limit && (
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                    Showing top {limit} of {entries.length} players
                </p>
            )}
        </div>
    );
};

export default Leaderboard;
